'use client'

import { useEffect, useState, type RefObject } from 'react'
import type { KeenSliderInstance } from 'keen-slider/react'

/**
 * Keen often measures before layout/fonts settle, so slides look broken until
 * something forces `update()`. Remeasure on mount, resize, and font load;
 * expose `ready` to avoid flashing the unmeasured track.
 */
export function useKeenSliderReady(
  instanceRef: RefObject<KeenSliderInstance | null>,
  layoutKey?: string | number,
) {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let cancelled = false
    let rafWait = 0
    let rafMeasure = 0
    let rafResize = 0
    let observer: ResizeObserver | null = null

    setReady(false)

    const refresh = () => {
      const slider = instanceRef.current
      if (!slider) return
      slider.update()
      if (!cancelled) setReady(true)
    }

    const scheduleResizeUpdate = () => {
      if (rafResize) return
      rafResize = requestAnimationFrame(() => {
        rafResize = 0
        if (!cancelled) instanceRef.current?.update()
      })
    }

    const attach = () => {
      const node = instanceRef.current?.container
      if (!node) {
        rafWait = requestAnimationFrame(attach)
        return
      }

      rafWait = requestAnimationFrame(() => {
        rafMeasure = requestAnimationFrame(refresh)
      })

      if (typeof ResizeObserver !== 'undefined') {
        observer = new ResizeObserver(scheduleResizeUpdate)
        observer.observe(node)
      }

      const fonts = document.fonts
      if (fonts?.ready) {
        void fonts.ready.then(() => {
          if (!cancelled) instanceRef.current?.update()
        })
      }
    }

    attach()

    return () => {
      cancelled = true
      cancelAnimationFrame(rafWait)
      cancelAnimationFrame(rafMeasure)
      cancelAnimationFrame(rafResize)
      observer?.disconnect()
    }
  }, [instanceRef, layoutKey])

  return ready
}

/** Hide unmeasured track; fade in once Keen has real widths. */
export function keenSliderReadyClass(ready: boolean) {
  return ready ? 'opacity-100' : 'opacity-0'
}
